import pluralize from 'pluralize'

import { Banner } from '@/components/ui/banner'
import type { BulkMoveHolder, BulkMovePreview } from '../../../../models/coi'

/** At most this many names before the rest become a count. */
const NAMES_SHOWN = 5

function Names({ holders }: { holders: BulkMoveHolder[] }) {
  const shown = holders.slice(0, NAMES_SHOWN)
  const rest = holders.length - shown.length
  return (
    <span>
      {shown.map((holder) => holder.name).join(', ')}
      {rest > 0 && ` and ${rest} more`}
    </span>
  )
}

/**
 * Everything the move would do beyond repointing holders, stated before it runs.
 *
 * Conflicts block: the unique constraint on (org, selection_key, email) would
 * reject them, so nothing is written until they are resolved. The rest are
 * consequences worth knowing rather than reasons to stop.
 */
export function BulkMoveWarnings({ preview }: { preview: BulkMovePreview }) {
  if (!preview.holderCount) {
    return <p className="text-muted-foreground text-sm">That certificate has no holders to move.</p>
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">
        <span className="font-medium">{pluralize('holder', preview.holderCount, true)}</span> would move onto
        v{preview.targetVersion} and have their certificate regenerated.
      </p>

      {!!preview.conflicts.length && (
        <Banner variant="error">
          {pluralize('holder', preview.conflicts.length, true)} share an email address with a holder already
          on the target certificate, so the move cannot run: <Names holders={preview.conflicts} />. Merge or
          remove them first.
        </Banner>
      )}

      {!!preview.losingSchedule.length && (
        <Banner variant="warning">
          The target certificate takes no vehicle schedule, so{' '}
          {pluralize('holder', preview.losingSchedule.length, true)} will stop receiving one:{' '}
          <Names holders={preview.losingSchedule} />.
        </Banner>
      )}

      {!!preview.manual.length && (
        <Banner variant="warning">
          {pluralize('holder', preview.manual.length, true)} {preview.manual.length === 1 ? 'has a' : 'have'}{' '}
          manually uploaded {pluralize('certificate', preview.manual.length)} that nothing will regenerate, so{' '}
          {preview.manual.length === 1 ? 'it' : 'they'} will still state the old coverage until you upload a
          replacement: <Names holders={preview.manual} />.
        </Banner>
      )}

      {!!preview.overlaps.length && (
        <Banner>
          {pluralize('address', preview.overlaps.length, true)} would end up holding the target certificate
          twice, so that inbox receives it more than once:{' '}
          {preview.overlaps
            .slice(0, NAMES_SHOWN)
            .map((holder) => holder.address)
            .join(', ')}
          .
        </Banner>
      )}
    </div>
  )
}
